/**
 * F1 (Sprint 16) — the mid-quiz income question + partner-income toggle.
 *
 * This is a NON-scored interstitial: it is deliberately NOT part of
 * QUESTION_BANK_V2 (which is scored and drift-locked against the doc). It
 * collects two bucket labels for cohort analysis only — the result
 * calculation does not consume them as a dollar multiplier yet (ADR-031.1 /
 * B2 backlog) and they are never shown on the result page.
 *
 * The buckets, copy, and position live here as the single source of truth
 * shared by the client card (components/quiz/income-question-card.tsx), the
 * server validation (app/api/quiz/income/route.ts), and the tests.
 */

export const INCOME_QUESTION_TEXT =
  "What's your monthly take-home pay (after fixed bills)?";

export const INCOME_QUESTION_HELPER =
  "Roughly — your post-tax income minus rent/mortgage and recurring bills.";

export const PARTNER_INCOME_CHECKBOX_LABEL =
  "Include my partner's income (shared household)";

export const PARTNER_INCOME_SELECT_LABEL = "Your partner's monthly take-home pay";

/**
 * Bucket labels, low → high. These exact strings are what we persist to
 * `income_bucket` / `partner_income_bucket` and send in the
 * `quiz_income_answered` event, so they double as the validation allow-list.
 */
export const INCOME_BUCKETS = [
  "<$1,500",
  "$1,500–2,500",
  "$2,500–4,000",
  "$4,000–6,000",
  "$6,000–9,000",
  "$9,000+",
] as const;

export type IncomeBucket = (typeof INCOME_BUCKETS)[number];

export function isIncomeBucket(value: unknown): value is IncomeBucket {
  return (
    typeof value === "string" &&
    (INCOME_BUCKETS as ReadonlyArray<string>).includes(value)
  );
}

/**
 * 1-based screen position: the income card is shown AFTER this many scored
 * questions have been answered (so it lands ~5th of ~21 screens — past the
 * low-friction opener, before fatigue sets in). Forward-only, like every
 * other quiz screen.
 */
export const INCOME_CARD_AFTER_INDEX = 4;
